import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Batch } from '@/schemas/inventory';

interface BatchesState {
  byId: Record<string, Batch>;
  allIds: string[];
  filters: {
    warehouseId?: string;
    variantId?: string;
    status?: string;
  };
  selectedBatchId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: BatchesState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedBatchId: null,
  isLoading: false,
  error: null,
};

const batchesSlice = createSlice({
  name: 'batches',
  initialState,
  reducers: {
    setBatches: (state, action: PayloadAction<Batch[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((batch) => {
        state.byId[batch.id] = batch;
        state.allIds.push(batch.id);
      });
    },
    addBatch: (state, action: PayloadAction<Batch>) => {
      const batch = action.payload;
      state.byId[batch.id] = batch;
      if (!state.allIds.includes(batch.id)) {
        state.allIds.push(batch.id);
      }
    },
    updateBatch: (state, action: PayloadAction<Batch>) => {
      const batch = action.payload;
      state.byId[batch.id] = batch;
    },
    deleteBatch: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setSelectedBatchId: (state, action: PayloadAction<string | null>) => {
      state.selectedBatchId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<BatchesState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setBatches,
  addBatch,
  updateBatch,
  deleteBatch,
  setSelectedBatchId,
  setFilters,
  setLoading,
  setError,
} = batchesSlice.actions;

export default batchesSlice.reducer;
